import {inject, Injectable} from '@angular/core';
import {LocalAnalysis} from "../../core/models/local-analysis";
import {filter, firstValueFrom, forkJoin, from, map, of, switchMap, tap, throwError} from "rxjs";
import {Analysis} from "../../core/models/analysis";
import {CustomError} from "../../core/models/error";
import {PictureStatus} from "../../core/models/picture";
import {TyreAnalysisRequestDto} from "../../core/dto/tyre-analysis-request-dto";
import {StepType} from "../../core/models/step";
import * as Sentry from "@sentry/angular";
import {Utils} from "../utils/generics.utils";
import {TyreAnalysisResponseDto} from "../../core/dto/tyre-analysis-response-dto";
import {AnalysisStatusEnum} from "../../core/models/analysis-status";
import {ReferenceApiService} from "../../core/api/reference-api/reference-api.service";
import {PictureUploadService} from "./picture-upload.service";
import {ApiService} from "../../core/api/api.service";
import {IndexedDBService} from "./indexed-db.service";
import {PostcodeService} from "../../features/shared/postcode/postcode.service";
import {TyrePicture} from "../../core/models/tyrePicture";
import {Product} from "../../core/models/product";
import {I18nService} from "./i18n.service";
import {AppStateService} from "../../core/state/app-state.service";
import {PhotoshootService} from "../../features/shared/photoshoot/services/photoshoot.service";
import {ExternalApiService} from "../../core/api/external-api/external-api.service";
import {PostAnalysisDto} from "../../core/dto/post-analysis-dto";
import {PostAnalysisResponseDto} from "../../core/dto/post-analysis-response-dto";
import {Vehicle} from "../../core/models/vehicle";

@Injectable({
  providedIn: 'root'
})
export class AnalysisService {

  protected referenceApi = inject(ReferenceApiService);
  protected externalApi = inject(ExternalApiService);
  protected postcodeService = inject(PostcodeService);
  protected uploadService = inject(PictureUploadService);
  protected api = inject(ApiService);
  protected indexedDB = inject(IndexedDBService);
  protected languageService = inject(I18nService);
  protected appStateService = inject(AppStateService);
  protected photoshootService = inject(PhotoshootService);

  createTyreAnalysis(picture: TyrePicture) {
    const analysisId = this.appStateService.analysis()?.id;
    const dto = new TyreAnalysisRequestDto('jpg', picture.step.position, picture.step.type);
    const vehicleId = this.appStateService.vehicle()?.id;

    if (vehicleId) {
      if (analysisId) {
        return this.referenceApi.getAnalysis(vehicleId, this.appStateService.product()).pipe(switchMap((analysis) => {
          const steps = picture.step.type === StepType.REFERENCE ? analysis.referenceTyres : analysis.wearTyres;
          const tyreAnalysis = steps.find((ta) => ta.position === picture.step.position);

          if (tyreAnalysis) {
            Sentry.captureMessage('Tried to create an existing tyre analysis', {extra: {picture: Utils.sanitizePictures(picture), tyreAnalysis}});
            return of({
              ...tyreAnalysis,
              id: tyreAnalysis.tyreAnalysisId
            } as TyreAnalysisResponseDto);
          } else {
            return this.referenceApi.createTyreAnalysis(analysisId, dto);
          }
        }))
      } else {
        return from(this.createAnalysisAndVehicle()).pipe(switchMap((analysis) => this.referenceApi.createTyreAnalysis(analysis.id!, dto)))
      }
    } else {
      return throwError(() => new Error(CustomError.NO_SUCH_VEHICLE));
    }
  }

  abortAnalysis() {
    const analysisId = this.appStateService.analysis()?.id;

    if (!analysisId) {
      return throwError(() => new Error(CustomError.NO_ANALYSIS));
    }

    return this.referenceApi.updateAnalysis(analysisId, AnalysisStatusEnum.ABORTED).pipe(
      tap(() => this.uploadService.cancelUpload()),
      tap(() => this.appStateService.clearLocalPicturesAndAnalysis()),
      switchMap(() => {
        if (this.appStateService.product() === Product.COLLECT) {
          return from(this.appStateService.clear()).pipe(map(() => null));
        } else {
          return from(this.createAnalysisAndVehicle());
        }
      })
    );
  }


  getAnalysis() {
    const vehicleId = this.appStateService.vehicle()?.id;
    if (!vehicleId) {
      return throwError(() => new Error(CustomError.NO_SUCH_VEHICLE));
    }

    return this.referenceApi.getAnalysis(vehicleId, this.appStateService.product()!).pipe(
      switchMap((response) => {
        if (response.status === AnalysisStatusEnum.UPLOADED) {
          return throwError(() => new Error(CustomError.ONGOING_ANALYSIS));
        } else {
          const analysis = new Analysis(response);
          this.appStateService.patch({analysis});
          const pictures = [
            ...analysis.referenceTyres.map(rt => TyrePicture.fromAnalysis(rt, StepType.REFERENCE)),
            ...analysis.wearTyres.map(wt => TyrePicture.fromAnalysis(wt, StepType.WEAR))
          ];
          this.photoshootService.setPictures(pictures);
          return of(analysis);
        }
      })
    );
  }

  private handleLocalAnalysis(analysis: Analysis, localAnalysis?: LocalAnalysis) {
    if (localAnalysis) {
      return from(this.createSpareTyre(analysis, localAnalysis)).pipe(
        switchMap(() => this.indexedDB.getAllTyresPictures()),
        switchMap((pictures) => {
          return this.getPicturesAndCreateTyreAnalyses(pictures, analysis)
        }),
        switchMap((value) => this.uploadPictures(value))
      );
    }
    return of(analysis);
  }

  private createSpareTyre(analysis: Analysis, localAnalysis: LocalAnalysis) {
    if (analysis.id && localAnalysis.spareTyreResult?.spareTyreResult) {
      return this.referenceApi.createSpareTyreAnalysis$(analysis.id).pipe(
        map((response) => response.id),
        filter((vehicleAnalysisId) => !!vehicleAnalysisId),
        switchMap((vehicleAnalysisId) => this.referenceApi.updateSpareTyreAnalysis$(vehicleAnalysisId, localAnalysis.spareTyreResult!.spareTyreResult!).pipe(
          map(() => vehicleAnalysisId))
        ),
        switchMap((vehicleAnalysisId) => this.referenceApi.updateVehicleAnalysis$(vehicleAnalysisId, AnalysisStatusEnum.UPLOADED).pipe(
          map(() => vehicleAnalysisId))
        ),
        tap((vehicleAnalysisId) =>
          // @ts-ignore
          this.appStateService.patch( {analysis: {
            ...this.appStateService.analysis(),
            spareTyreResult: {
              vehicleAnalysisId,
              spareTyreResult: localAnalysis.spareTyreResult!.spareTyreResult!,
              status: AnalysisStatusEnum.UPLOADED
            }
          }}))
      );
    } else {
      return of(undefined);
    }
  }

  private getPicturesAndCreateTyreAnalyses(pictures: TyrePicture[], analysis: Analysis) {
    pictures = pictures.map((p) => {
      p.status = PictureStatus.UPLOADING;
      return p;
    });
    this.indexedDB.changePicturesStatus(PictureStatus.UPLOADING);
    const promises: Promise<TyreAnalysisResponseDto>[] = [];
    for (const picture of pictures) {
      promises.push(firstValueFrom(this.createTyreAnalysis(picture)));
    }
    return forkJoin([...promises, of(pictures), of(analysis)]);
  }

  private uploadPictures(value: [...TyreAnalysisResponseDto[], TyrePicture[], Analysis]) {
    const analysis = value.pop() as Analysis;
    const pictures = value.pop() as TyrePicture[];
    const responses = value as TyreAnalysisResponseDto[];
    pictures.forEach((picture, index) => {
      picture.uploadInfo = responses[index];
      this.photoshootService.setPicture(picture);
      this.uploadService.uploadPicture(picture);
    });
    return of(analysis);
  }

  async createAnalysisAndVehicle(localAnalysis?: LocalAnalysis) {
    const {
      vehicle = this.appStateService.vehicle(),
      product = this.appStateService.product(),
      company = this.appStateService.company(),
      fleet = this.appStateService.fleet(),
      postcode = this.appStateService.postcode(),
      externalId = this.appStateService.externalId()
    } = localAnalysis || {};

    if (company?.key) {
      const dto = {
        language: this.languageService.getLanguage(),
        appOrigin: Utils.getAppOrigin(product),
        companyKey: company.key,
        contextOrigin: fleet,
        externalId: externalId,
        vehicleMileage: vehicle?.mileage,
        license: vehicle?.license?.license,
        licenseCountry: vehicle?.license?.countryISO,
        contact: this.appStateService.contact(),
        inseeCodeTravelZone: postcode?.codeCommune,
        zipCodeTravelZone: postcode?.codePostal,
      } as PostAnalysisDto;
      const analysisResponse = await firstValueFrom(this.externalApi.createAnalysis(dto));
      const analysis = this.handleAnalysisCreation(analysisResponse);
      if (localAnalysis) {
        await firstValueFrom(this.handleLocalAnalysis(analysis, localAnalysis));
      }
      return analysisResponse;
    } else {
      return Promise.reject(new Error(CustomError.NO_SUCH_COMPANY))
    }
  }

  private handleAnalysisCreation(analysisResponse: PostAnalysisResponseDto) {
    const analysis = Analysis.create(analysisResponse);
    this.photoshootService.clearPictures();
    const vehicle = Vehicle.create(analysisResponse.vehicle.id, analysisResponse.vehicle.license, analysisResponse.vehicle.licenseCountry);
    this.appStateService.patch({analysis, vehicle});
    return analysis;
  }
}
